/*
 * Le manifeste du carnet doit designer UN restaurant : `start_url` porte son
 * uuid, sinon l'icone posee sur l'ecran d'accueil ouvre une application qui ne
 * sait pas quelle salle elle sert (sur iOS, une application installee a son
 * propre stockage, vide : rien a relire pour deviner le restaurant).
 *
 * Il est donc rendu a la demande a partir d'un gabarit unique,
 * `public/waiter-manifest.template.json`, par DEUX serveurs qui doivent rester
 * d'accord :
 *   - en dev, le plugin `waiterAppEndpoints` de vite.config.ts (ce fichier) ;
 *   - en preprod et en prod, nginx (`nginx/default.conf`, sub_filter).
 * Toucher au gabarit ou au chemin ci-dessous impose de verifier les deux.
 */

/** Le meme motif que RestaurantRouteGuard : un uuid, rien d'autre. */
export const RESTAURANT_UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Marqueur remplace par l'uuid du restaurant, cote dev comme cote nginx. */
export const UUID_PLACEHOLDER = '__RESTAURANT_UUID__';

/*
 * Le nom du restaurant sur l'icone : un meme serveur travaille dans plusieurs
 * etablissements, et trois icones identiques sur un ecran d'accueil ne se
 * distinguent pas. `name` est ce que montre la boite d'installation,
 * `short_name` ce qui tient sous l'icone (une douzaine de caracteres, tronques
 * au-dela) — d'ou le nom seul la, et « <nom> Serveur » ici.
 */
export const APP_NAME_PLACEHOLDER = '__RESTAURANT_APP_NAME__';
export const APP_SHORT_NAME_PLACEHOLDER = '__RESTAURANT_APP_SHORT__';

/** Ce que porte l'icone quand le restaurant n'est pas (encore) connu. */
export const DEFAULT_APP_NAME = 'Carnet serveur';
export const DEFAULT_APP_SHORT_NAME = 'Serveur';

/** Au-dela, ni la boite d'installation ni l'ecran d'accueil ne montrent rien. */
export const MAX_APP_NAME_LENGTH = 40;

export const MANIFEST_TEMPLATE_FILE = 'waiter-manifest.template.json';

/*
 * Le nom voyage DANS l'adresse du manifeste, en clair : ni nginx ni le plugin
 * Vite ne savent quel restaurant porte quel uuid, et un manifeste fabrique dans
 * le navigateur (blob:) n'est pas installable — l'usine a WebAPK de Chrome va
 * le chercher elle-meme, depuis ses serveurs. Le segment est donc nettoye ici
 * de tout ce qui casserait soit le chemin, soit le JSON du gabarit.
 */
export function waiterAppLabel(restaurantName: string | null | undefined): string {
  if (typeof restaurantName !== 'string') return '';
  const collapsed = restaurantName
    // Hors du domaine imprimable, un caractere casserait le JSON du gabarit.
    .replace(/[^ -~\u00a0-\uffff]/g, ' ')
    // Une barre oblique, droite ou inverse, sortirait de l'adresse ; le
    // guillemet casserait le JSON ; `%` `#` `?` changeraient le sens de l'URL.
    .replace(/["%#?\\/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  // Coupe par caractere, pas par unite UTF-16 : un emoji a cheval sur la limite
  // laisserait un demi-caractere, et encodeURIComponent leverait (DF-0563).
  const cleaned = Array.from(collapsed).slice(0, MAX_APP_NAME_LENGTH).join('').trim();
  // Un nom fait de points seuls (« .. ») serait replie par nginx sur un autre
  // chemin, qui repondrait l'index.html en 200 — un manifeste invalide.
  return /[^.]/.test(cleaned) ? cleaned : '';
}

/**
 * Adresse du manifeste rendu pour ce restaurant. Sans nom (le temps que la
 * fiche du restaurant arrive, ou si elle n'arrive pas), c'est l'ancienne
 * adresse : le manifeste reste servi, sous un nom generique.
 */
export function waiterManifestPath(restaurantUuid: string, restaurantName?: string | null): string {
  const label = waiterAppLabel(restaurantName);
  const base = `/${restaurantUuid}/serveur`;
  return label
    ? `${base}/${encodeURIComponent(label)}/manifest.webmanifest`
    : `${base}/manifest.webmanifest`;
}

/*
 * Le worker est servi SOUS l'uuid : la portee d'un service worker ne peut pas
 * remonter au-dessus du dossier qui l'heberge. A la racine il gouvernerait
 * toutes les pages de l'origine (les commandes des clients comprises) ; ici il
 * couvre `/<uuid>/`, ce qui inclut le carnet et les tables qu'il ouvre — le
 * minimum pour que Chrome juge le `start_url` installable.
 */
export function waiterServiceWorkerPath(restaurantUuid: string): string {
  return `/${restaurantUuid}/sw.js`;
}

/**
 * Le rendu de reference — celui que nginx et le plugin Vite recopient chacun
 * dans son langage. Le suffixe est fige ici : le manifeste est rendu par un
 * serveur, qui ne sait pas dans quelle langue le serveur lit son telephone.
 */
export function renderWaiterManifest(
  template: string,
  restaurantUuid: string,
  restaurantName?: string | null,
): string {
  const label = waiterAppLabel(restaurantName);
  return template
    .split(UUID_PLACEHOLDER)
    .join(restaurantUuid)
    .split(APP_NAME_PLACEHOLDER)
    .join(label ? label + ' ' + DEFAULT_APP_SHORT_NAME : DEFAULT_APP_NAME)
    .split(APP_SHORT_NAME_PLACEHOLDER)
    .join(label || DEFAULT_APP_SHORT_NAME);
}
